import React, { useEffect, useRef, useState } from "react";
import { ResponsiveFunnel } from "@nivo/funnel";
import styles from "./FunnelChart.module.css";

const DEFAULT_SKELETON_STAGES = 4;
const SKELETON_MAX_WIDTH = 85; // % width of the first stage
const SKELETON_MIN_WIDTH = 30; // % width of the last stage

const FunnelLabels = ({ parts }) => (
  <g className={styles.labels} pointerEvents="none">
    {parts.map((part) => {
      const { data } = part;
      const label = data.label ?? data.id;
      const percent = data.percent ?? 0;
      const textColor = data.labelColor || "#19315f";
      return (
        <g key={data.id} transform={`translate(${part.x}, ${part.y})`}>
          <text className={styles.stageLabel} fill={textColor} y="-7">
            {label}
          </text>
          <text className={styles.stageValue} fill={textColor} y="14">
            <tspan>{Number(data.value).toLocaleString()}</tspan>
            <tspan className={styles.stagePercent} dx="7">
              {`${Number(percent).toFixed(1)}%`}
            </tspan>
          </text>
        </g>
      );
    })}
  </g>
);

const FunnelTooltip = ({ part }) => (
  <div className={styles.tooltip}>
    <span className={styles.tooltipMarker} style={{ background: part.color }} />
    <span className={styles.tooltipLabel}>
      {part.data.label ?? part.data.id}
    </span>
    <strong>{Number(part.data.value).toLocaleString()}</strong>
    <span className={styles.tooltipPercent}>
      {`${Number(part.data.percent ?? 0).toFixed(1)}%`}
    </span>
  </div>
);

/**
 * Shimmering placeholder funnel shown while data is loading. It fills the same
 * height as the chart, so nothing jumps when the real chart appears.
 */
const FunnelSkeleton = ({ stages, ariaLabel }) => {
  const widths = Array.from({ length: stages }, (_, i) =>
    stages > 1
      ? SKELETON_MAX_WIDTH -
        i * ((SKELETON_MAX_WIDTH - SKELETON_MIN_WIDTH) / (stages - 1))
      : SKELETON_MAX_WIDTH,
  );

  return (
    <div className={styles.skeleton} aria-busy="true" aria-label={ariaLabel}>
      {widths.map((width, index) => (
        <span
          key={index}
          className={styles.skeletonStage}
          style={{ width: `${width}%` }}
        />
      ))}
    </div>
  );
};

/**
 * Responsive Nivo funnel. Each datum accepts id, label, value, color, and an
 * optional funnelWidth ratio (shape width relative to the chart) and percent.
 * While `loading` is true, a shimmering skeleton funnel is shown instead.
 */
const FunnelChart = ({
  data = [],
  height = 280,
  className = "",
  ariaLabel = "Funnel chart",
  footer,
  onDatumClick,
  loading = false,
}) => {
  const chartRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    const chartElement = chartRef.current;
    if (!chartElement) return undefined;

    const updateWidth = () => {
      setChartWidth(chartElement.clientWidth);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(chartElement);
    return () => observer.disconnect();
  }, []);

  const chartData = data.map((item) => ({
    ...item,
    label: item.label ?? String(item.id),
    percent: item.percent ?? 0,
    funnelWidth: item.funnelWidth ?? 0.85,
  }));

  return (
    <div className={[styles.container, className].filter(Boolean).join(" ")}>
      <div ref={chartRef} className={styles.chart} style={{ height }}>
        {loading ? (
          <FunnelSkeleton
            stages={data.length || DEFAULT_SKELETON_STAGES}
            ariaLabel={`Loading ${ariaLabel.toLowerCase()}`}
          />
        ) : (
          <ResponsiveFunnel
            data={chartData}
            direction="vertical"
            interpolation="linear"
            spacing={3}
            shapeBlending={0}
            size={(datum) => Math.max(1, chartWidth * datum.funnelWidth)}
            colors={{ datum: "color" }}
            borderWidth={0}
            enableLabel={false}
            enableBeforeSeparators={false}
            enableAfterSeparators={false}
            isInteractive
            currentPartSizeExtension={10}
            currentBorderWidth={2}
            onClick={(part) => onDatumClick?.(part.data)}
            tooltip={FunnelTooltip}
            animate
            motionConfig="gentle"
            layers={["parts", FunnelLabels]}
            margin={{ top: 0, right: 4, bottom: 0, left: 4 }}
            role="img"
            ariaLabel={ariaLabel}
          />
        )}
      </div>
      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );
};

export default FunnelChart;